import { Checkbox } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, ShowMoreButton, Spacer, Typography } from "@aviary";
import { SuggestedCertifications } from "@shared/components/CatalogSearch/Filters/Certifications/SuggestedCertifications/SuggestedCertifications";
import { useThirdPartyCertificationsSharedQuery } from "@shared/data/queries/ThirdPartyCertificationsQuery/ThirdPartyCertifications.query.generated";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import type { Filter } from "../../CatalogSearch";
import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./Certifications.styles";

type Certification = Pick<
  Types.ThirdPartyCertificationContextualFilters,
  "id" | "esResultCount"
> & {
  thirdPartyCertification: Pick<Types.ThirdPartyCertification, "id" | "name">;
};
type NonContextualCertification = Pick<Types.ThirdPartyCertification, "id" | "name">;

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isContextual?: boolean;
  contextualData?: Certification[];
}

const Certifications = ({
  isBold,
  logFilterEvent,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const { activeFilters, setActiveFilters } = useCatalogSearch();
  const { data, loading } = useThirdPartyCertificationsSharedQuery();
  const [showAll, setShowAll] = useState(false);

  const showLessCutoff = 5;

  if (
    isContextual
      ? (contextualData?.length ?? 0) === 0
      : loading || !data?.viewer?.thirdPartyCertifications
  ) {
    return null;
  }

  const suggestedCertifications: Certification[] = (contextualData ?? []).slice(0, 5);
  const allCertifications: Certification[] = contextualData ?? [];

  const renderCertificationLabel = (certificationWithCount: Certification) => {
    return `${certificationWithCount.thirdPartyCertification.name} (${certificationWithCount.esResultCount})`;
  };

  const renderAllCertifications = () => {
    const sortedAllCertifications = [...allCertifications];
    sortedAllCertifications.sort((a: Certification, b: Certification) =>
      a.thirdPartyCertification.name
        .toLowerCase()
        .localeCompare(b.thirdPartyCertification.name.toLowerCase())
    );

    return sortedAllCertifications?.map(certificationWithCount => {
      if (showAll) {
        return (
          <span key={certificationWithCount.thirdPartyCertification.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={isCertificationActive(certificationWithCount.thirdPartyCertification.id)}
              onChange={() =>
                handleCertificationSelect({
                  name: certificationWithCount.thirdPartyCertification.name,
                  id: certificationWithCount.thirdPartyCertification.id,
                  category: "Certifications",
                })
              }
            >
              {renderCertificationLabel(certificationWithCount)}
            </Checkbox>
          </span>
        );
      }

      return null;
    });
  };

  const renderBottomShowMoreButton = () => {
    if (!showAll) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // old filters that don't include result count
  const nonContextualCertificationFilters: NonContextualCertification[] =
    data?.viewer?.thirdPartyCertifications ?? [];

  const renderNonContextualCertificationLabel = (certification: NonContextualCertification) => {
    return certification.name;
  };

  const renderAllNonContextualCertifications = () =>
    nonContextualCertificationFilters?.map((certification, index) => {
      if (showAll || index < showLessCutoff) {
        return (
          <span key={certification.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={isCertificationActive(certification.id)}
              onChange={() =>
                handleCertificationSelect({
                  name: certification.name,
                  id: certification.id,
                  category: "Certifications",
                })
              }
            >
              {renderNonContextualCertificationLabel(certification)}
            </Checkbox>
          </span>
        );
      }

      return null;
    });

  const renderNonContextualShowMoreButton = () => {
    if ((nonContextualCertificationFilters?.length ?? 0) <= showLessCutoff) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // shared logic for both contextual and non-contextual filters
  const isCertificationActive = (id: string): boolean =>
    activeFilters.some(activeCertification => activeCertification.id === id);

  const handleCertificationSelect = (certificationSelected: Filter) => {
    const distanceFromTop = window?.scrollY;
    if (isCertificationActive(certificationSelected.id)) {
      setActiveFilters(
        activeFilters.filter(certification => certification.name !== certificationSelected.name)
      );
    } else {
      logFilterEvent(certificationSelected.name, FilterType.CERTIFICATIONS);
      setActiveFilters([...activeFilters, certificationSelected]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderTopShowMoreButton = () => {
    if (isContextual && !showAll) {
      return (
        <>
          <Spacer height="half" />
          <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
        </>
      );
    }

    return null;
  };

  const renderHeader = () => {
    if (isContextual) {
      if (showAll) {
        return (
          <Typography type="h5" css={styles.header}>
            {t(l.sharedCatalog.filter.AllCertifications)}
          </Typography>
        );
      }

      return null;
    }

    return <Typography type="h5">{t(l.sharedCatalog.filter.AllCertifications)}</Typography>;
  };

  const renderAllCertificationsSection = () => {
    if (
      isContextual
        ? allCertifications.length <= 5
        : (data?.viewer?.thirdPartyCertifications?.length ?? 0) <= 5
    ) {
      return null;
    }

    return (
      <>
        {renderTopShowMoreButton()}
        {renderHeader()}
        <div css={styles.checkboxList}>
          {isContextual ? renderAllCertifications() : renderAllNonContextualCertifications()}
          {isContextual ? renderBottomShowMoreButton() : renderNonContextualShowMoreButton()}
        </div>
      </>
    );
  };

  const renderSuggestedCertifications = () => {
    if (!isContextual) {
      return null;
    }

    return (
      <SuggestedCertifications
        certifications={suggestedCertifications}
        handleCertificationSelect={handleCertificationSelect}
        isCertificationActive={isCertificationActive}
        renderCertificationLabel={renderCertificationLabel}
      />
    );
  };

  const activeCertificationCount = activeFilters.filter(
    active => active?.category === "Certifications"
  )?.length;

  return (
    <Accordion
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Certifications)}
          count={activeCertificationCount}
          isSecondaryWeight={isBold}
          isBadge
        />
      }
    >
      {renderSuggestedCertifications()}

      {renderAllCertificationsSection()}
    </Accordion>
  );
};

export { Certifications };
